import { useState, useEffect } from 'react'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { Eye, Download } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { colors } from '@/styles/colors'
import AuditLogDetailsModal from './AuditLogDetailsModal'
import Paginator from '@/components/Paginator'

interface AuditLog {
  id: string
  event_type: string
  event_category: string
  resource_type: string
  resource_id: string | null
  resource_name: string | null
  actor_id: string
  actor_email: string | null
  actor_name: string | null
  uploaded_by_id: string | null
  uploaded_by_name: string | null
  ip_address: string | null
  user_agent: string | null
  action_description: string
  created_at: string
  metadata: { user_agent?: string } & Record<string, unknown>
  agent_context: Record<string, unknown> | null
}

function useEventTypeConfig() {
  const { t } = useTranslation()
  return {
    document_upload: {
      label: t('common:audit_log.event_types.uploading'),
      textClass: 'text-blue-700 dark:text-blue-400',
      bgClass: 'bg-blue-100 dark:bg-blue-900/30',
      dotClass: 'bg-blue-500',
    },
    document_delete: {
      label: t('common:audit_log.event_types.deleting'),
      textClass: 'text-red-700 dark:text-red-400',
      bgClass: 'bg-red-100 dark:bg-red-900/30',
      dotClass: 'bg-red-500',
    },
    document_update: {
      label: t('common:audit_log.event_types.updates'),
      textClass: 'text-orange-700 dark:text-orange-400',
      bgClass: 'bg-orange-100 dark:bg-orange-900/30',
      dotClass: 'bg-orange-500',
    },
    document_view: {
      label: t('common:audit_log.event_types.viewing'),
      textClass: 'text-gray-700 dark:text-gray-300',
      bgClass: 'bg-gray-100 dark:bg-dark-accent',
      dotClass: 'bg-gray-500',
    },
    document_download: {
      label: t('common:audit_log.event_types.downloading'),
      textClass: 'text-teal-700 dark:text-teal-400',
      bgClass: 'bg-teal-100 dark:bg-teal-900/30',
      dotClass: 'bg-teal-500',
    },
    document_metadata_update: {
      label: t('common:audit_log.event_types.changing'),
      textClass: 'text-teal-700 dark:text-teal-400',
      bgClass: 'bg-teal-100 dark:bg-teal-900/30',
      dotClass: 'bg-teal-500',
    },
  }
}

export default function AuditLog() {
  const { t } = useTranslation()
  const EVENT_TYPE_CONFIG = useEventTypeConfig()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [total, setTotal] = useState(0)
  // FS-505: the filter + search are persisted to sessionStorage so the
  // selection survives this component unmounting — which happens every
  // time the user switches away from the Logs tab and back, since
  // SettingsPage only renders the active tab's subtree. Without this the
  // filter silently resets to the default ("Action" → unfiltered list)
  // and reads as the filter "changing on its own". Mirrors how
  // SettingsPage already persists the active tab via sessionStorage.
  const [eventFilter, setEventFilter] = useState<string>(
    () => sessionStorage.getItem('audit-log-event-filter') ?? ''
  )
  const [searchQuery, setSearchQuery] = useState<string>(
    () => sessionStorage.getItem('audit-log-search') ?? ''
  )

  useEffect(() => {
    sessionStorage.setItem('audit-log-event-filter', eventFilter)
  }, [eventFilter])

  useEffect(() => {
    sessionStorage.setItem('audit-log-search', searchQuery)
  }, [searchQuery])

  const fetchAuditLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page_number: (page - 1).toString(),
        page_size: pageSize.toString(),
      })

      if (eventFilter) {
        params.append('event_type', eventFilter)
      }

      if (searchQuery) {
        params.append('search', searchQuery)
      }

      const response = await apiService.fetchOrError(
        apiService.getUrl(`v1/audit/logs?${params.toString()}`)
      )
      const data = await response.json()
      setLogs(data.logs || [])
      setTotal(data.total || 0)
    } catch (error) {
      console.error('Failed to fetch audit logs:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAuditLogs()
  }, [page, pageSize, eventFilter, searchQuery])

  const getEventConfig = (eventType: string) => {
    return (
      EVENT_TYPE_CONFIG[eventType as keyof typeof EVENT_TYPE_CONFIG] || {
        label: eventType,
        textClass: 'text-gray-700 dark:text-gray-300',
        bgClass: 'bg-gray-100 dark:bg-dark-accent',
        dotClass: 'bg-gray-500',
      }
    )
  }

  const [exporting, setExporting] = useState(false)

  const exportLogs = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams()
      if (eventFilter) params.append('event_type', eventFilter)
      if (searchQuery) params.append('search', searchQuery)

      const response = await apiService.fetchOrError(
        apiService.getUrl(`v1/audit/logs/export?${params.toString()}`)
      )
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('common:audit_log.export_failed', 'Failed to export audit logs'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">{t('common:audit_log.title')}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
            {t('common:audit_log.description')}
          </p>
        </div>
        <button
          onClick={exportLogs}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: colors.blue[600] }}
          onMouseEnter={(e) => {
            if (!exporting) e.currentTarget.style.backgroundColor = colors.blue[700]
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.blue[600]
          }}
        >
          <Download className="w-4 h-4" />
          {exporting ? t('common:audit_log.exporting', 'Exporting...') : t('common:audit_log.export_logs')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <select
          value={eventFilter}
          onChange={(e) => {
            setEventFilter(e.target.value)
            setPage(1)
          }}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-dark-card dark:text-dark-text"
        >
          <option value="">{t('common:audit_log.action_filter')}</option>
          <option value="document_upload">{t('common:audit_log.event_types.uploading')}</option>
          <option value="document_delete">{t('common:audit_log.event_types.deleting')}</option>
          <option value="document_update">{t('common:audit_log.event_types.updates')}</option>
          <option value="document_view">{t('common:audit_log.event_types.viewing')}</option>
          <option value="document_download">{t('common:audit_log.event_types.downloading')}</option>
          <option value="document_metadata_update">{t('common:audit_log.event_types.changing')}</option>
        </select>

        <div className="flex-1 relative">
          <input
            type="text"
            placeholder={t('common:audit_log.search_placeholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            className="w-full px-4 py-2 text-sm bg-white dark:bg-dark-card dark:text-dark-text border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-dark-muted border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.user')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.action')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.date_time')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.subject')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.uploaded_by')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.browser')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('common:audit_log.table_headers.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-100 dark:divide-dark-border">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-gray-500 dark:text-dark-text-muted">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-gray-500 dark:text-dark-text-muted">
                    {t('common:audit_log.no_logs')}
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const eventConfig = getEventConfig(log.event_type)
                  return (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                            {log.actor_name || t('common:audit_log.unknown')}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-dark-text-muted">{log.actor_email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                            eventConfig.textClass,
                            eventConfig.bgClass
                          )}
                        >
                          <span
                            className={cn('w-1.5 h-1.5 rounded-full', eventConfig.dotClass)}
                          />
                          {eventConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-900 dark:text-dark-text">
                            {new Date(log.created_at).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                            {new Date(log.created_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                            })}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900 dark:text-dark-text">
                          {log.resource_name ? `"${log.resource_name}"` : log.action_description}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900 dark:text-dark-text">
                          {log.uploaded_by_id
                            ? log.uploaded_by_name || t('common:audit_log.unknown')
                            : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {log.metadata?.user_agent?.includes('Chrome')
                            ? 'Chrome'
                            : log.metadata?.user_agent?.includes('Safari')
                              ? 'Safari'
                              : log.metadata?.user_agent?.includes('Firefox')
                                ? 'Mozilla'
                                : log.metadata?.user_agent?.includes('Edge')
                                  ? 'Edge'
                                  : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <Paginator
            currentPage={page}
            totalPages={Math.ceil(total / pageSize)}
            totalItems={total}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        )}
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <AuditLogDetailsModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  )
}
